import { useContext, useEffect, useRef } from "react";

import useLogger from "@hd/logger/useLogger";

import { HubContext, HubContextCallbacks } from "./hub-context";
import { HubStatus } from "./hub-status";
import { useHubStore } from "./hub.store";
import { SignalrChatMessageDto } from "@hd/types/dto/contact/signalr-chat-message.dto";
import { ContactConnectionInfoDto } from "@hd/types/dto/contact/contact-connection-info.dto";
import { SignalrRequestLockedDto } from "@hd/types/dto/contact/signalr-request-locked.dto";
import { SignalrRequestUnlockedDto } from "@hd/types/dto/contact/signalr-request-unlocked.dto";

export const useHub = (
  companyId: number | null,
  callbacks: HubContextCallbacks,
  customUserId?: string
) => {
  const context = useContext(HubContext);
  const hubStatus = useHubStore((state) => state.hubStatus);

  if (!context) {
    throw new Error("useHub must be used within a HubProvider");
  }

  const { subscribe, unsubscribe } = context;

  const { debug } = useLogger();

  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (companyId && companyId > 0) {
      const useHubCallbacks: HubContextCallbacks = {
        onNewMessage: (message: SignalrChatMessageDto) =>
          callbacksRef.current.onNewMessage?.(message),
        onPersonStatusUpdated: (message: ContactConnectionInfoDto) =>
          callbacksRef.current.onPersonStatusUpdated?.(message),
        onMessagesRead: (messageIds: string[]) =>
          callbacksRef.current.onMessagesRead?.(messageIds),
        onQuotationLocked: (quotationData: SignalrRequestLockedDto) =>
          callbacksRef.current.onQuotationLocked?.(quotationData),
        onQuotationUnlocked: (quotationData: SignalrRequestUnlockedDto) =>
          callbacksRef.current.onQuotationUnlocked?.(quotationData),
        onRequestLocked: (quotationData: SignalrRequestLockedDto) =>
          callbacksRef.current.onRequestLocked?.(quotationData),
        onRequestUnlocked: (quotationData: SignalrRequestUnlockedDto) =>
          callbacksRef.current.onRequestUnlocked?.(quotationData),
      };
      subscribe(companyId, useHubCallbacks, customUserId).then(() => {
        debug("listening on connection", companyId);
      });

      return () => {
        unsubscribe(companyId, useHubCallbacks, customUserId).then(() => {
          debug("stop listening on connection", companyId);
        });
      };
    }
  }, [companyId, customUserId, subscribe, unsubscribe, debug]);

  return {
    hubStatus:
      companyId != null ? hubStatus[companyId] : HubStatus.Disconnected,
  };
};
